import React from "react";
import styled from "styled-components";
import { Tooltip } from "@mantine/core"; // Import Mantine's Tooltip
import CustomNavLink from "../components/Lib/CustomNavLink"; // Ensure correct import path
import useLayoutStore from "../stores/useLayoutStore";
import { useMediaQuery } from "@mantine/hooks";

const NavLinksContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-radius: 10px;
  background: #fff;
  padding: 10px;
`;

const NavLinksWrapper = styled.nav`
  margin-bottom: 20px;
`;

const StyledNavLink = styled(CustomNavLink)`
  padding: 10px;
  text-decoration: none;
  color: #000;
  border-radius: 6px;
  font-size: 1rem;
  display: block;

  &.active {
    background: #f6f8ff;
    color: var(--color-button-accent);
    font-weight: var(--font-weight-body-2);
  }

  &:hover {
    background: #f6f8ff;
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }
`;

const InnerNavLink = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
  transition: background-color 0.3s, color 0.3s;

  &.active::before {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    width: 3px;
    background-color: var(--color-button-accent);
  }
`;

const NavIcon = styled.img`
  width: 20px;
  height: 20px;
  margin-right: 10px;
  transition: filter 0.3s;

  ${StyledNavLink}.active & {
    filter: none;
  }

  & {
    filter: brightness(0) grayscale(0);
  }
`;

const NavDivider = styled.div`
  border-top: 1px solid #dce5e4;
  margin: 10px 0;
`;

const DisabledLinkWrapper = styled.div`
  padding: 10px;
  text-decoration: none;
  color: #000;
  border-radius: 6px;
  font-size: 1rem;
  display: block;
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
`;

const NavLinks = ({ links: { links, active, icons } }) => {
  const { isToggle } = useLayoutStore(); // Get Zustand state
  const isMobile = useMediaQuery("max-width: 834px")
  const tooltipStyles = {
    tooltip: {
      backgroundColor: "#F6F8FF",
      color: "var(--color-button-accent)",
      border: "1px solid var(--color-button-accent)",
    },
  };

  return (
    <NavLinksContainer $isToggle={isToggle}>
      <NavLinksWrapper>
        {links.map((link, index) => (
          <React.Fragment key={index}>
            {link.comingSoon ? (
              <Tooltip
                label="Coming Soon"
                position="right"
                withArrow
                offset={11}
                transitionProps={{ transition: 'pop-bottomo-left', duration: 300 }}
                styles={tooltipStyles}
              >
                <DisabledLinkWrapper>
                  <InnerNavLink $isToggle={isToggle}>
                    {icons ? (
                      <div>
                        <link.icon />
                      </div>
                    ) : (
                      <NavIcon
                        src={link.icon}
                        alt={`${link.name} icon`}
                        $isToggle={isToggle}
                      />
                    )}
                    {/* Show text only when not collapsed */}
                    {!isToggle && <span>{link.name}</span>}
                  </InnerNavLink>
                </DisabledLinkWrapper>
              </Tooltip>
            ) : (
              <StyledNavLink
                to={link.href}
                className={link.name.includes(active) ? "active" : ""}
                $isToggle={isToggle}
              >
                <InnerNavLink
                  className={link.name.includes(active) ? "active" : ""}
                  $isToggle={isToggle}
                >
                  {icons ? (
                    isToggle ? (
                      <Tooltip
                        label={link.name}
                        position="right"
                        withArrow
                        offset={11}
                        transitionProps={{ transition: 'pop-bottomo-left', duration: 300 }}
                        styles={tooltipStyles}
                      >
                        <div>
                          <link.icon />
                        </div>
                      </Tooltip>
                    ) : (
                      <div>
                        <link.icon />
                      </div>
                    )
                  ) : (
                    isToggle ? (
                      <Tooltip
                        label={link.name}
                        position="right"
                        withArrow
                        offset={11}
                        styles={tooltipStyles}
                        transitionProps={{ transition: 'pop-bottomo-left', duration: 300 }}
                      >
                        <NavIcon
                          src={link.icon}
                          alt={`${link.name} icon`}
                          $isToggle={isToggle}
                        />
                      </Tooltip>
                    ) : (
                      <NavIcon
                        src={link.icon}
                        alt={`${link.name} icon`}
                        $isToggle={isToggle}
                      />
                    )
                  )}
                  {/* Show text only when not collapsed */}
                  {(!isToggle || isMobile) && <span>{link.name}</span>}
                </InnerNavLink>
              </StyledNavLink>
            )}
            {index === 5 && <NavDivider />}
          </React.Fragment>
        ))}
      </NavLinksWrapper>
    </NavLinksContainer>
  );
};

export default NavLinks;
